import { useState } from 'react'
import PropTypes from 'prop-types'

import theme from 'theme'
import TextButton from 'components/common/TextButton'
import InviteUsersModal from 'components/rbac-settings/InviteUsersModal'
import useAuth from 'hooks/use-auth'
import TitleWithIcon from 'components/common/TitleWithIcon'
import NavIcon from 'components/navigation/NavIcon'
import Text from 'components/common/Text'
import Icon from 'components/Icon'

const UsersIcon = props => <Icon icon="users" {...props} />

export function isSoloUser(users, currentUser) {
  return users.length === 1 && users[0].identifier === currentUser
}

function getUserInitials({ user, label }) {
  if (label) return label

  return user.first_name || user.last_name ? (
    `${(user.first_name || [])[0] || ''}${(user.last_name || [])[0] || ''}`
  ) : (
    <Icon
      css={{
        fontSize: theme.fontSizes[4]
      }}
      icon="users"
    />
  )
}

getUserInitials.propTypes = {
  user: PropTypes.object,
  label: PropTypes.string
}

function UserInitialsIcon({ user, userIndex, label, ...rest }) {
  //  There are potentially 6 circles max
  // with display flex all you need to do is to decrement the margin by one for each circles
  return (
    <div
      key={`user group icons ${label ? label : user.identifier}`}
      css={{
        marginLeft: userIndex === 0 || !userIndex ? 0 : -theme.space[2],
        zIndex: -(userIndex || 0)
      }}
      data-testid="dashboard-user-initial-icon"
    >
      <NavIcon
        css={{
          marginLeft: 0,
          width: theme.space[5],
          height: theme.space[5],
          fontSize: theme.fontSizes[0],
          color: theme.colors.steel[0],
          cursor: 'default',
          border: '2px solid',
          borderColor: theme.colors.panel[0],
          '&:hover': {
            color: theme.colors.steel[0],
            backgroundColor: theme.colors.steel[2]
          }
        }}
        {...rest}
      >
        {getUserInitials({ user, label })}
      </NavIcon>
    </div>
  )
}

UserInitialsIcon.propTypes = {
  user: PropTypes.object,
  label: PropTypes.string,
  userIndex: PropTypes.number
}

function UserGroupIcons({ users, isSoloUser }) {
  if (users.length === 0 || isSoloUser) {
    return null
  }

  const maxUserIconsCount = Math.min(5, users.length)
  const remainingUsersCount = users.length - maxUserIconsCount
  const displayUserList = users.slice(0, maxUserIconsCount)

  return (
    <div
      css={{
        display: 'flex',
        justifyContent: 'flex-end'
      }}
    >
      {displayUserList.map((user, index) => {
        return (
          <UserInitialsIcon
            key={`user group icons ${user.identifier}`}
            user={user}
            userIndex={index}
          />
        )
      })}
      {/* Add the remaining count if any */}
      {remainingUsersCount > 0 && (
        <UserInitialsIcon
          key="user group icons remains"
          css={{
            backgroundColor: theme.colors.gray[2],
            color: theme.colors.steel[2],
            border: '2px solid',
            borderColor: theme.colors.panel[0]
          }}
          data-testid="dashboard-user-remaining-icon"
          label={`+${remainingUsersCount}`}
          userIndex={maxUserIconsCount}
        />
      )}
    </div>
  )
}

UserGroupIcons.propTypes = {
  isSoloUser: PropTypes.bool,
  users: PropTypes.array
}

export default function TeamSection({
  doFetch,
  company,
  users,
  isLoading,
  ...rest
}) {
  const { hasElevatedCompanyRole, currentTeam, session } = useAuth()
  const [inviteUsersModalOpen, setInviteUsersModalOpen] = useState(false)

  return (
    <div
      css={{
        zIndex: 0,
        display: 'grid',
        gap: theme.space[1],
        justifyItems: 'end'
      }}
      {...rest}
      data-testid="dashboard-team-header"
    >
      <TitleWithIcon
        as="h4"
        css={{
          justifyContent: 'flex-end'
        }}
        data-testid="dashboard-team-name"
        icon={UsersIcon}
        label={currentTeam.name}
      />
      {isLoading ? (
        <Text>Fetching users...</Text>
      ) : (
        <UserGroupIcons
          isSoloUser={isSoloUser(users, session.identifier)}
          users={users}
        />
      )}
      {hasElevatedCompanyRole && (
        <div>
          <TextButton
            css={{
              display: 'inline-block',
              fontSize: theme.fontSizes[0],
              textAlign: 'right',
              fontWeight: theme.fontWeights[1]
            }}
            onClick={() => setInviteUsersModalOpen(true)}
          >
            + Invite your team members
          </TextButton>
          <InviteUsersModal
            company={company}
            data-testid="dashboard-invite-user-modal"
            doFetch={doFetch}
            isOpen={inviteUsersModalOpen}
            setIsOpen={setInviteUsersModalOpen}
          />
        </div>
      )}
    </div>
  )
}

TeamSection.propTypes = {
  doFetch: PropTypes.func,
  company: PropTypes.object,
  users: PropTypes.array,
  isLoading: PropTypes.bool
}

TeamSection.defaultProps = {
  users: []
}
